import { ROUTES } from '@aibees/elephant/router';
import { DialogTitle, Image } from '@aibees/shared/components';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useInsufficientCreditStore } from '@aibees/shared/store';
import { classnames, fCredit } from '@aibees/shared/utils';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Styled from './styles';
import * as SX from './sx';
import type { InsufficientCreditsDialogProps as Props } from './types';

export const InsufficientCreditsDialog = memo(({ className }: Props) => {
  const navigate = useNavigate();
  const { data } = useCurrentSubscriptionApi();

  const { isError, setIsError } = useInsufficientCreditStore();

  const handleClose = () => {
    setIsError(false);
  };

  const handleUpgrade = () => {
    handleClose();
    navigate(ROUTES.priceAndPlan);
  };

  return (
    <Styled.Wrapper
      open={isError}
      className={classnames('InsufficientCreditsDialog', className)}
      sx={SX.root}
      fullWidth={true}
      maxWidth="xs"
    >
      <DialogTitle
        title="Insufficient Credits"
        icon="problem"
        onClose={handleClose}
      />
      <DialogContent>
        <Stack gap={1}>
          <Box sx={SX.imageWrapper}>
            <Image src="/assets/images/insufficient-credits-2.png" />
            <Typography sx={SX.creditTotal}>
              {fCredit(data?.credit?.total || 0)}
            </Typography>
            <Typography sx={SX.creditRollover}>
              {fCredit(Number(data?.current?.plan?.metadata.rollover) || 0)}
            </Typography>
          </Box>
          <Typography variant="body3" fontWeight={700} color="grey.700">
            You need just a few more credits to complete this action.
          </Typography>
          <Stack gap={0.5}>
            <Typography variant="body3" fontWeight={600} color="grey.700">
              To continue, Please consider:
            </Typography>
            <Stack direction="row" alignItems="center" sx={SX.option}>
              <Typography variant="body4" fontWeight={700} color="grey.700">
                01
              </Typography>
              <Typography variant="body4" fontWeight={500} color="grey.700">
                Adjust the number of records to fit within your remaining
                credits.
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" sx={SX.option}>
              <Typography variant="body4" fontWeight={700} color="grey.700">
                02
              </Typography>
              <Typography variant="body4" fontWeight={500} color="grey.700">
                Upgrade your plan
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleUpgrade}
          >
            🚀 Upgrade Now
          </Button>
        </Stack>
      </DialogActions>
    </Styled.Wrapper>
  );
});

InsufficientCreditsDialog.displayName = 'InsufficientCreditsDialog';
